import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, Link } from 'react-router-dom';
import {
  Activity, ArrowRight, ChartColumn, ChevronDown, CircleAlert, CircleCheck, Download,
  RefreshCw, TrendingDown, Users, Wallet,
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import { getCourseAnalytics, getCourseLessonCompletion, exportCourseAnalytics } from '../../api/analytics';
import LoadingState from '../../components/LoadingState/LoadingState';
import PageHead from '../../components/PageHead/PageHead';
import ProgressBar from '../../components/ProgressBar/ProgressBar';
import PageEmptyState from '../../components/PageEmptyState/PageEmptyState';
import InlineEmpty from '../../components/InlineEmpty/InlineEmpty';
import TableCard from '../../components/TableCard/TableCard';
import MetricCard from '../../components/MetricCard/MetricCard';
import DateRangePicker from '../../components/DatePicker/DateRangePicker';
import {
  BAR_CURSOR_PROPS, CHART_MARGIN, ChartFrame, ChartTooltip, GRID_PROPS, X_AXIS_PROPS, Y_AXIS_PROPS,
  barProps,
} from '../../components/charts/chartTheme';
import { popoverRoot, useAnchoredPopover } from '../../hooks/useAnchoredPopover';
import { useCourseTitle } from '../../hooks/useCourseTitle';
import { useI18n } from '../../i18n/I18nContext';
import {
  formatDuration, formatMoney, formatMoneyCompact, formatNumber, formatPercent, formatTime,
} from '../../utils/datetime';
import './CourseAnalytics.css';

// Keys are the fields GET /analytics/courses/:id returns. The money tile goes last: "2.6 mln so'm"
// needs a wider tile than a count, so it takes the odd last place of a phone's two-up row (and
// two columns on a wide screen) instead of breaking inside the figure.
const KPI_DEFS = [
  { key: 'totalEnrollments', labelKey: 'courseInsights.analytics.kpiEnrollments', Icon: Users, tone: 'info', fmt: (v) => formatNumber(v || 0, { decimals: 0 }) },
  { key: 'activeEnrollments', labelKey: 'courseInsights.analytics.kpiActiveStudents', Icon: Activity, tone: 'success', fmt: (v) => formatNumber(v || 0, { decimals: 0 }) },
  { key: 'completedEnrollments', labelKey: 'courseInsights.analytics.kpiCompleted', Icon: CircleCheck, tone: 'purple', fmt: (v) => formatNumber(v || 0, { decimals: 0 }) },
  { key: 'avgCompletion', labelKey: 'courseInsights.analytics.kpiAvgCompletion', Icon: ChartColumn, tone: 'teal', fmt: (v) => formatPercent(v || 0, { decimals: 0 }) },
  { key: 'totalRevenue', labelKey: 'courseInsights.analytics.kpiRevenue', Icon: Wallet, tone: 'brand', money: true, fmt: (v) => formatMoneyCompact(v || 0) },
];

// A handful of lessons read fine as the table below; the chart earns its place once the course is
// long enough that the shape of the fall-off is worth seeing at a glance.
const CHART_MIN_LESSONS = 5;
// More rows than this and the table caps its height, so its head stays in view while it scrolls.
const TALL_ROWS = 12;

const clampRate = (value) => Math.max(0, Math.min(100, Number(value) || 0));

/**
 * The CSV export: one quiet button in the toolbar that opens a small panel with the two things
 * worth choosing (the student list, the lesson figures) and the download itself. The range the
 * page shows is the range that is exported.
 */
function ExportMenu({ options, onChange, onExport, exporting }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const popRef = useRef(null);
  const pos = useAnchoredPopover(open, setOpen, anchorRef, popRef, { align: 'end' });

  // Keyboard users land on the first choice, in the same commit the panel is placed in.
  const placed = Boolean(pos);
  useEffect(() => {
    if (!open || !placed) return;
    const first = popRef.current?.querySelector('input');
    if (first && typeof first.focus === 'function') first.focus({ preventScroll: true });
  }, [open, placed]);

  const download = async () => {
    await onExport();
    setOpen(false);
  };

  return (
    <div ref={anchorRef} className="an-export">
      <button
        type="button"
        className="ds-btn ds-btn-secondary"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <Download aria-hidden="true" />
        <span>{t('courseInsights.analytics.exportButton')}</span>
        <ChevronDown className="an-export-chevron" aria-hidden="true" />
      </button>
      {open && createPortal(
        <div
          ref={popRef}
          className="ui-picker-panel an-export-panel"
          role="dialog"
          aria-label={t('courseInsights.analytics.exportTitle')}
          style={pos ? { top: pos.top, left: pos.left, zIndex: pos.zIndex } : { top: 0, left: 0, opacity: 0, pointerEvents: 'none' }}
        >
          <h3 className="an-export-title">{t('courseInsights.analytics.exportTitle')}</h3>
          <p className="an-export-desc">{t('courseInsights.analytics.exportDesc')}</p>
          <label className="ui-switch-row">
            <span>{t('courseInsights.analytics.includeStudents')}</span>
            <input
              type="checkbox"
              role="switch"
              className="ui-switch"
              checked={options.includeStudents}
              onChange={(e) => onChange({ includeStudents: e.target.checked })}
            />
          </label>
          <label className="ui-switch-row">
            <span>{t('courseInsights.analytics.includeLessons')}</span>
            <input
              type="checkbox"
              role="switch"
              className="ui-switch"
              checked={options.includeLessons}
              onChange={(e) => onChange({ includeLessons: e.target.checked })}
            />
          </label>
          <button
            type="button"
            onClick={download}
            disabled={exporting}
            className={`ds-btn ds-btn-primary an-export-go${exporting ? ' is-loading' : ''}`}
          >
            <Download aria-hidden="true" />
            <span>{exporting ? t('courseInsights.analytics.exporting') : t('courseInsights.analytics.downloadCsv')}</span>
          </button>
        </div>,
        popoverRoot(anchorRef.current),
      )}
    </div>
  );
}

function CourseAnalytics({ courseId: courseIdProp, embedded: embeddedProp } = {}) {
  const { t } = useI18n();
  const params = useParams();
  const courseId = courseIdProp || params.courseId || params.id;
  // Inside the course workspace the tab already names the page; the standalone route carries
  // its own header.
  const embedded = embeddedProp ?? Boolean(courseIdProp);
  const courseTitle = useCourseTitle(embedded ? null : courseId);
  const [analytics, setAnalytics] = useState(null);
  const [lessonMetrics, setLessonMetrics] = useState([]);
  // The lesson table is secondary: when only it fails to load, the KPIs stay and it says so.
  const [lessonsFailed, setLessonsFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [denied, setDenied] = useState(false);
  const [cacheTime, setCacheTime] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [exportOptions, setExportOptions] = useState({
    includeStudents: true,
    includeLessons: true,
  });

  // Only the newest request may update the page: the range changes fast, and an older, slower
  // answer must not overwrite the figures of the range now on screen.
  const latestRequest = useRef(0);

  const fetchAnalytics = useCallback(async () => {
    latestRequest.current += 1;
    const requestId = latestRequest.current;
    try {
      setLoading(true);
      const query = {};
      if (startDate) query.startDate = startDate;
      if (endDate) query.endDate = endDate;
      const [data, lessons] = await Promise.all([
        getCourseAnalytics(courseId, query),
        // The lesson table is secondary: the KPIs still show if it cannot load
        getCourseLessonCompletion(courseId, query).catch(() => null),
      ]);
      if (requestId !== latestRequest.current) return;
      setAnalytics(data);
      setLessonsFailed(lessons === null);
      setLessonMetrics([...(lessons?.lessons || [])].sort((a, b) => a.sequenceIndex - b.sequenceIndex));
      setCacheTime(new Date());
      setError(null);
      setDenied(false);
    } catch (err) {
      if (requestId !== latestRequest.current) return;
      setDenied(err.code === 403);
      setError(err.code === 403
        ? t('courseInsights.analytics.accessDenied')
        : t('courseInsights.analytics.errorLoad'));
    } finally {
      if (requestId === latestRequest.current) setLoading(false);
    }
  }, [courseId, startDate, endDate, t]);

  // Another course's figures must never stay on screen under this course while its own load
  useEffect(() => {
    setAnalytics(null);
    setLessonMetrics((prev) => (prev.length ? [] : prev));
    setLessonsFailed(false);
  }, [courseId]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await exportCourseAnalytics(courseId, {
        format: 'csv',
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
        ...exportOptions,
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `course-analytics-${courseId}-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch {
      setError(t('courseInsights.analytics.errorExport'));
    } finally {
      setExporting(false);
    }
  };

  const header = !embedded && (
    <PageHead
      back={{ to: `/courses/edit/${courseId}?tab=analytics`, label: t('courseInsights.common.backToCourse') }}
      eyebrow={<span className="an-eyebrow">{courseTitle || t('courseInsights.common.parent')}</span>}
      title={t('courseInsights.analytics.title')}
      description={t('courseInsights.analytics.subtitle')}
    />
  );

  // Only the first load replaces the page: a refetch for a new date range keeps the pickers
  // mounted, so choosing a day never loses the open panel or its focus.
  if (loading && !analytics) {
    return (
      <div className="ui-stack an-page">
        {header}
        <LoadingState skeleton="page" message={t('courseInsights.analytics.loading')} />
      </div>
    );
  }

  const chartRows = lessonMetrics.map((lesson, index) => ({
    n: index + 1,
    title: lesson.title,
    rate: clampRate(lesson.completionRate),
  }));
  const lessonLabel = (n) => `${n}. ${chartRows[n - 1]?.title || ''}`.trim();

  return (
    <div className="ui-stack an-page" aria-busy={loading}>
      {header}

      {/* Toolbar: the range, when it was last read, refresh */}
      <div className="an-toolbar">
        <DateRangePicker
          from={startDate}
          to={endDate}
          onChange={({ from, to }) => { setStartDate(from); setEndDate(to); }}
          fromLabel={t('courseInsights.analytics.fromDateAria')}
          toLabel={t('courseInsights.analytics.toDateAria')}
        />
        <div className="an-toolbar-end">
          {(startDate || endDate) && (
            <button type="button" className="ds-btn ds-btn-ghost" onClick={() => { setStartDate(''); setEndDate(''); }}>
              {t('courseInsights.analytics.clear')}
            </button>
          )}
          {cacheTime && (
            <span className="an-updated">
              {t('courseInsights.analytics.updated', { time: formatTime(cacheTime) })}
            </span>
          )}
          <button
            type="button"
            onClick={fetchAnalytics}
            className="ds-btn ds-btn-secondary ds-btn-icon"
            title={t('courseInsights.analytics.refreshTitle')}
            aria-label={t('courseInsights.analytics.refreshTitle')}
          >
            <RefreshCw className={loading ? 'an-spin' : undefined} aria-hidden="true" />
          </button>
          {analytics && (
            <ExportMenu
              options={exportOptions}
              onChange={(patch) => setExportOptions((prev) => ({ ...prev, ...patch }))}
              onExport={handleExport}
              exporting={exporting}
            />
          )}
        </div>
      </div>

      {error && analytics && (
        <div className="ui-alert" role="alert">
          <CircleAlert aria-hidden="true" />
          <p>{error}</p>
          <button type="button" className="ds-btn ds-btn-secondary ds-btn-sm" onClick={fetchAnalytics}>
            {t('ui.retry')}
          </button>
        </div>
      )}

      {!analytics && error && (
        <PageEmptyState
          variant={denied ? 'forbidden' : 'error'}
          description={error}
          onRetry={denied ? undefined : fetchAnalytics}
        />
      )}

      {analytics && (
        <>
          {/* KPI tiles */}
          <div className="kpi-row an-kpis">
            {KPI_DEFS.map(({ key, labelKey, Icon, tone, fmt, money }) => (
              <MetricCard
                key={key}
                className={money ? 'an-kpi--money' : undefined}
                title={t(labelKey)}
                value={fmt(analytics[key])}
                valueTitle={money ? formatMoney(analytics[key], 'UZS', { free: false }) : undefined}
                icon={<Icon />}
                tone={tone}
              />
            ))}
          </div>

          {/* Deep-dive pages */}
          <div className="an-navcards">
            <Link to={`/courses/${courseId}/analytics/students`} className="section-card is-interactive an-navcard">
              <span className="ui-icon-chip" data-tone="info"><Users aria-hidden="true" /></span>
              <span className="an-navcard-text">
                <strong>{t('courseInsights.analytics.navProgressTitle')}</strong>
                <span>{t('courseInsights.analytics.navProgressDesc')}</span>
              </span>
              <ArrowRight className="an-navcard-arrow" aria-hidden="true" />
            </Link>
            <Link to={`/courses/${courseId}/analytics/drop-off`} className="section-card is-interactive an-navcard">
              <span className="ui-icon-chip" data-tone="coral"><TrendingDown aria-hidden="true" /></span>
              <span className="an-navcard-text">
                <strong>{t('courseInsights.analytics.navDropoffTitle')}</strong>
                <span>{t('courseInsights.analytics.navDropoffDesc')}</span>
              </span>
              <ArrowRight className="an-navcard-arrow" aria-hidden="true" />
            </Link>
          </div>

          {/* Lesson completion: the shape of the course first, then every lesson in numbers */}
          {chartRows.length >= CHART_MIN_LESSONS && (
            <section className="section-card an-chart-card">
              <div className="section-head">
                <div>
                  <h2 className="section-head__title">{t('courseInsights.analytics.lessonChartTitle')}</h2>
                  <p className="section-head__desc">{t('courseInsights.analytics.lessonChartDesc')}</p>
                </div>
              </div>
              <ChartFrame data={chartRows} minPoints={1}>
                <BarChart data={chartRows} margin={CHART_MARGIN}>
                  <CartesianGrid {...GRID_PROPS} />
                  <XAxis dataKey="n" {...X_AXIS_PROPS} />
                  <YAxis
                    {...Y_AXIS_PROPS}
                    domain={[0, 100]}
                    ticks={[0, 25, 50, 75, 100]}
                    tickFormatter={(value) => formatPercent(value, { decimals: 0 })}
                  />
                  <Tooltip
                    cursor={BAR_CURSOR_PROPS}
                    content={(
                      <ChartTooltip
                        valueFormatter={(value) => formatPercent(value, { decimals: 0 })}
                        labelFormatter={lessonLabel}
                      />
                    )}
                  />
                  <Bar dataKey="rate" name={t('courseInsights.analytics.colCompletion')} {...barProps(0)} />
                </BarChart>
              </ChartFrame>
            </section>
          )}

          {/* Lesson performance */}
          <section className="an-lessons">
            <div className="section-head">
              <div>
                <h2 className="section-head__title">{t('courseInsights.analytics.lessonPerformanceTitle')}</h2>
              </div>
            </div>
            {lessonsFailed ? (
              <div className="ui-alert" role="alert">
                <CircleAlert aria-hidden="true" />
                <p>{t('courseInsights.analytics.errorLessons')}</p>
                <button type="button" className="ds-btn ds-btn-secondary ds-btn-sm" onClick={fetchAnalytics}>
                  {t('ui.retry')}
                </button>
              </div>
            ) : lessonMetrics.length === 0 ? (
              <InlineEmpty
                kind="empty"
                title={t('courseInsights.analytics.noLessonsTitle')}
                description={t('courseInsights.analytics.noLessonsDesc')}
              />
            ) : (
              <TableCard
                className="an-table-card"
                tall={lessonMetrics.length > TALL_ROWS}
                aria-label={t('courseInsights.analytics.lessonPerformanceTitle')}
              >
                <table className="ui-table ui-table--stack an-table">
                  <thead>
                    <tr>
                      <th>{t('courseInsights.analytics.colLesson')}</th>
                      <th className="ui-num">{t('courseInsights.analytics.colStarted')}</th>
                      <th className="ui-num">{t('courseInsights.analytics.colCompleted')}</th>
                      <th className="an-col-progress">{t('courseInsights.analytics.colCompletion')}</th>
                      <th className="ui-num">{t('courseInsights.analytics.colAvgTime')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lessonMetrics.map((lesson) => {
                      const rate = clampRate(lesson.completionRate);
                      return (
                        <tr key={lesson.lessonId}>
                          <td className="an-lesson">{lesson.title}</td>
                          <td className="ui-num" data-label={t('courseInsights.analytics.colStarted')}>
                            {formatNumber(lesson.studentsStarted || 0, { decimals: 0 })}
                          </td>
                          <td className="ui-num" data-label={t('courseInsights.analytics.colCompleted')}>
                            {formatNumber(lesson.studentsCompleted || 0, { decimals: 0 })}
                          </td>
                          <td data-label={t('courseInsights.analytics.colCompletion')}>
                            <div className={`an-progress${rate >= 100 ? ' is-complete' : ''}`}>
                              <ProgressBar className="an-bar" value={rate} label={t('courseInsights.analytics.colCompletion')} />
                              <span className="an-pct">{formatPercent(rate, { decimals: 0 })}</span>
                            </div>
                          </td>
                          <td className="ui-num an-time" data-label={t('courseInsights.analytics.colAvgTime')}>
                            {lesson.averageTime ? formatDuration(lesson.averageTime * 60, { style: 'long' }) : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </TableCard>
            )}
          </section>
        </>
      )}
    </div>
  );
}

export default CourseAnalytics;
